import {
  AddLeadMutation,
  AppDataQuery,
  DismissLeadMutation,
  DynamicallyFilteredLeadsQuery,
  TodoItemStatusType,
} from 'graphql/bindings/AdminBindings'
import {
  ArchiveIcon,
  BanIcon,
  CardTableRow,
  CheckIcon,
  CheckSquareIcon,
  PlusCircleIcon,
  Tooltip,
  Typography,
  UserPlusIcon,
} from '@forgeglobal/design-framework'
import { PopoverListItem, PopoverListItemIcon, PopoverListItemText } from 'isolated-design-library'
import { Routes } from 'util/Routes'
import { formatNetWorthRange } from 'util/EnumUtils'
import React, { Fragment, useState } from 'react'
import flag from 'country-code-emoji'
import moment from 'moment'

type AccountFromLeadsQuery = DynamicallyFilteredLeadsQuery['dynamicQuery']['data'][0]

interface RowActionsProps {
  lead: AccountFromLeadsQuery
  openAssignModal: (accountId: string) => void
  openProfileAccountModal: (accountId: string) => void
  addLead: (accountId: string) => Promise<AddLeadMutation['addLead'] | undefined>
  dismissLead: (accountId: string) => Promise<DismissLeadMutation['dismissLead'] | undefined>
  currentAdmin: AppDataQuery['currentAdmin'] | undefined
}

interface RowActionProps {
  icon: React.FC
  text: string
  onClick: () => void
}

const RowAction: React.FC<RowActionProps> = ({ icon: Icon, text, onClick }) => (
  <PopoverListItem button onClick={onClick}>
    <PopoverListItemIcon>
      <Icon />
    </PopoverListItemIcon>
    <PopoverListItemText>{text}</PopoverListItemText>
  </PopoverListItem>
)

const isUnassigned = ({ profilingAssignees }: AccountFromLeadsQuery) =>
  profilingAssignees && profilingAssignees.length === 0
const isAssignedToUser = ({ profilingAssignees }: AccountFromLeadsQuery, id: number) =>
  (profilingAssignees || []).some(assignee => assignee && assignee.id === id)
const statusIs = ({ leadStatus }: AccountFromLeadsQuery, statuses: TodoItemStatusType[]) =>
  leadStatus && statuses.includes(leadStatus)
const statusIsNot = (lead: AccountFromLeadsQuery, statuses: TodoItemStatusType[]) => !statusIs(lead, statuses)

const RowActions: React.FC<RowActionsProps> = ({
  lead,
  openAssignModal,
  openProfileAccountModal,
  addLead,
  dismissLead,
  currentAdmin,
}) => {
  const canAdd = lead.leadStatus === null
  const canAssign = statusIsNot(lead, [TodoItemStatusType.Completed]) && isUnassigned(lead)
  const canDismiss =
    statusIsNot(lead, [TodoItemStatusType.Completed, TodoItemStatusType.Dismissed]) &&
    currentAdmin &&
    (isUnassigned(lead) || isAssignedToUser(lead, currentAdmin.id))
  const canProfile = currentAdmin && isAssignedToUser(lead, currentAdmin.id)

  const assignText =
    statusIs(lead, [TodoItemStatusType.Live]) || !lead.leadStatus ? 'Assign profiling' : 'Reassign profiling'

  const actions: RowActionProps[] = [
    canAdd && { icon: PlusCircleIcon, text: 'Add to leads', onClick: () => addLead(lead.id).catch(() => {}) },
    canAssign && { icon: UserPlusIcon, text: assignText, onClick: () => openAssignModal(lead.id) },
    canDismiss && { icon: ArchiveIcon, text: 'Dismiss', onClick: () => dismissLead(lead.id).catch(() => {}) },
    canProfile && {
      icon: CheckSquareIcon,
      text: 'Complete profiling',
      onClick: () => openProfileAccountModal(lead.id),
    },
  ].filter((action): action is RowActionProps => !!action)

  return (
    <Fragment>
      {actions.map(props => (
        <RowAction key={props.text} {...props} />
      ))}
      {actions.length === 0 && (
        <PopoverListItem>
          <PopoverListItemText>No actions available.</PopoverListItemText>
        </PopoverListItem>
      )}
    </Fragment>
  )
}

interface LeadRowProps {
  lead: DynamicallyFilteredLeadsQuery['dynamicQuery']['data'][0]
  openAssignModal: (accountId: string) => void
  openProfileAccountModal: (accountId: string) => void
  addLead: (accountId: string) => Promise<AddLeadMutation['addLead'] | undefined>
  dismissLead: (accountId: string) => Promise<DismissLeadMutation['dismissLead'] | undefined>
  currentAdmin: AppDataQuery['currentAdmin'] | undefined
}

const truncate = (s: string) => (s.length > 25 ? s.slice(0, 25) + '...' : s)

export const LeadsTableRow: React.FC<LeadRowProps> = ({
  lead,
  openAssignModal,
  openProfileAccountModal,
  addLead,
  dismissLead,
  currentAdmin,
}) => {
  const [emailTooltipText, setEmailTooltipText] = useState('Copy')
  const personalEntities = lead.entities.filter(e => e.roles.find(r => r.type === 'personal'))

  const bids =
    lead.bid_companies.length === 0
      ? '-'
      : `${lead.bid_companies.length} (${lead.bid_companies.map(x => x.name).join(', ')})`

  const handleEmailClick = (email: string, event: React.MouseEvent<HTMLSpanElement, MouseEvent>) => {
    event.stopPropagation()
    navigator.clipboard.writeText(email).then(() => {
      setEmailTooltipText('Copied')
      setTimeout(() => {
        setEmailTooltipText('Copy')
      }, 1000)
    })
  }

  const { email, name } = lead
  const countryOfDomicile = lead.entities[0].domicile_country_code

  const renderedColumns = {
    name: (
      <div style={{ paddingTop: 10, paddingBottom: 10 }}>
        <a
          style={{ color: 'inherit', textDecoration: 'none' }}
          href={Routes.account(lead.id)}
          target="_blank"
          rel="noopener noreferrer">
          <Typography>{name}</Typography>
        </a>

        <Tooltip title={emailTooltipText} placement={'right'}>
          <div style={{ maxWidth: '65%' }}>
            <Typography variant="body2" color="textSecondary" onClick={event => handleEmailClick(email ?? '', event)}>
              {email}
            </Typography>
          </div>
        </Tooltip>
      </div>
    ),
    net_worth: <Typography>{lead.net_worth && formatNetWorthRange(lead.net_worth)}</Typography>,
    target_check_size: (
      <Typography>
        {lead.investor_preferences?.target_check_size &&
          formatNetWorthRange(lead.investor_preferences?.target_check_size)}
      </Typography>
    ),
    email_verified: lead.user?.email_verified ? <CheckIcon color="secondary" /> : <BanIcon />,
    accredited:
      personalEntities.length > 0 && personalEntities[0].is_accredited ? <CheckIcon color="secondary" /> : <BanIcon />,
    bids: <Typography title={bids}>{truncate(bids)}</Typography>,
    last_contacted_by: <Typography>{lead.last_outreach_by ? lead.last_outreach_by?.name : '-'}</Typography>,
    country: (
      <Typography>{countryOfDomicile !== null ? `${countryOfDomicile} ${flag(countryOfDomicile)}` : '-'}</Typography>
    ),
    date_created: <Typography>{moment(lead.created_at).format('MM/DD/YY')}</Typography>,
  }

  return (
    <CardTableRow
      key={lead.id}
      id={lead.id}
      columns={renderedColumns}
      actionsMenu={
        <RowActions
          lead={lead}
          openAssignModal={openAssignModal}
          openProfileAccountModal={openProfileAccountModal}
          addLead={addLead}
          dismissLead={dismissLead}
          currentAdmin={currentAdmin}
        />
      }
    />
  )
}
